import { Dispatch, SetStateAction } from 'react';

import { AspectRatio, ImageForDropzone, ImageInput } from 'app/shared/typings';

interface OriginalFileInfo {
  width: number;
  height: number;
  url: string;
}

const getCropJSONForOriginalFile = (originalFileInfo: OriginalFileInfo) => {
  return {
    inputPath: originalFileInfo.url,
    pipeline: {
      steps: [
        {
          geometry: {
            offset: { x: 0, y: 0 },
            size: {
              width: originalFileInfo.width,
              height: originalFileInfo.height,
              type: 'widthxheight!',
            },
          },
          type: 'crop',
        },
      ],
    },
  };
};

async function fetchUploadIOCropJSON(
  originalFileInfo: OriginalFileInfo,
  cropFileLocation?: string
) {
  if (!cropFileLocation) {
    return getCropJSONForOriginalFile(originalFileInfo);
  }

  try {
    const response = await fetch(cropFileLocation);
    return await response.json();
  } catch (error) {
    console.error(error);
  }
}

async function fetchImageDimensions(fileLocation: string) {
  const img = new Image();
  img.src = fileLocation;
  await img.decode();
  return [img.naturalWidth, img.naturalHeight];
}

function translateUploadIOToImgIXParams({
  crop_horizontal_offset,
  crop_vertical_offset,
  crop_width,
  crop_height,
  originalFileWidth,
  originalFileHeight,
  ratio,
}: {
  crop_horizontal_offset: number;
  crop_vertical_offset: number;
  crop_width: number;
  crop_height: number;
  originalFileWidth: number;
  originalFileHeight: number;
  ratio: AspectRatio;
}) {
  const originalImageRatio = originalFileWidth / originalFileHeight;
  const cropImageRatio = crop_width / crop_height;
  const fp_x = (
    (crop_horizontal_offset + crop_width / 2) /
    originalFileWidth
  ).toPrecision(4);
  const fp_y = (
    (crop_vertical_offset + crop_height / 2) /
    originalFileHeight
  ).toPrecision(4);
  const fp_z = (
    cropImageRatio < originalImageRatio
      ? originalFileHeight / crop_height
      : originalFileWidth / crop_width
  ).toPrecision(4);

  return `?fit=crop&crop=focalpoint&w=${crop_width}&h=${crop_height}&fp-x=${fp_x}&fp-y=${fp_y}&fp-z=${fp_z}&ar=${ratio}`;
}

export async function calculateImgIXParams({
  setImgIXParamsMap,
  imagesForAllRatios,
}: {
  setImgIXParamsMap: Dispatch<SetStateAction<Record<string, string>>>;
  imagesForAllRatios: ImageForDropzone[];
}) {
  imagesForAllRatios.map(async (image) => {
    const [originalFileWidth, originalFileHeight] = await fetchImageDimensions(
      image.originalFileUrl || ''
    );
    const originalFileInfo = {
      width: originalFileWidth,
      height: originalFileHeight,
      url: image.originalFileUrl,
    };

    const uploadIOCropJSON = await fetchUploadIOCropJSON(
      originalFileInfo,
      image.editedFileUrl
    );

    if (uploadIOCropJSON && originalFileWidth && originalFileHeight) {
      const cropInstructions = uploadIOCropJSON.pipeline.steps;
      const {
        offset: { x: crop_horizontal_offset, y: crop_vertical_offset },
        size: { width: crop_width, height: crop_height },
      } = cropInstructions[0].geometry;

      const imgIXParams = translateUploadIOToImgIXParams({
        crop_horizontal_offset,
        crop_vertical_offset,
        crop_width,
        crop_height,
        originalFileWidth,
        originalFileHeight,
        ratio: image.ratio,
      });

      setImgIXParamsMap((currentImgIXParamsMap) => {
        return {
          ...currentImgIXParamsMap,
          [image.fileUrl]: imgIXParams,
        };
      });
    }
  });
}

interface Props {
  imagesForAllRatios: ImageForDropzone[];
  imgIXParamsMap: Record<string, string>;
}

export function getImagesToSave({
  imagesForAllRatios,
  imgIXParamsMap,
}: Props): ImageInput[] {
  return imagesForAllRatios.map((image) => {
    // originalFileName = e.g. City-29-1.3333333333333333-1700176971453-image.jpg
    const originalFileName = image.originalFileUrl.split('/').pop();
    const filePlatform = 'imgix';
    const imgIXParams = imgIXParamsMap[image.fileUrl] || '';
    const filename = originalFileName + imgIXParams;

    return {
      contentType: image.mime,
      filePlatform,
      filename,
      ratio: image.ratio,
      size: image.size,
      title: image.title,
      description: image.description,
    };
  });
}
